import { apiGetQuoteDetailInfo } from '@/cms/api/quoteApi';
import {
  InsuranceProviderResponse,
  MessageGetResponse,
  MessageUpsertRequest,
  PromotionGetResponse,
  QuoteResponse,
  User,
  apiCreateInternalQuoteMessage,
  apiCreateQuoteMessage,
  apiDeleteQuoteMessage,
  apiGetInsuranceProviders,
  apiGetQuote,
  apiGetQuoteMessages,
  apiGetUser,
  apiUpdateAdminQuote,
  apiUpdateQuoteMessage,
  jove__app__quote__to_model__QuoteUpdateRequest,
} from '@jove-frontend/uikit';
import isEmpty from 'lodash/isEmpty';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface IQuoteState {
  isLoading: boolean;
  quote: QuoteResponse;
  assignee: User;
  quoteMessages: MessageGetResponse[];
  insuranceProviders: InsuranceProviderResponse[];
}

export interface IQuoteActions {
  initialize: (id: string) => Promise<void>;
  loadData: (id: string) => Promise<void>;
  fetchQuote: (id: string) => Promise<boolean>;
  fetchAssignee: () => Promise<void>;
  fetchQuoteMessages: () => Promise<boolean>;
  fetchInsuranceProviders: () => Promise<void>;
  getInsuranceProvider: (providerCode: string) => InsuranceProviderResponse | null;
  setAssignee: (user: User | null) => void;
  updateQuote: (payload: jove__app__quote__to_model__QuoteUpdateRequest) => Promise<void>;
  createQuoteMessage: (payload: MessageUpsertRequest, external?: boolean) => Promise<boolean>;
  updateQuoteMessage: (messageId: string, payload: MessageUpsertRequest) => Promise<boolean>;
  deleteQuoteMessage: (messageId: string) => Promise<boolean>;
}

interface IQuoteStore {
  state: IQuoteState;
  actions: IQuoteActions;
  clearState: () => void;
}

export const useQuoteStore = create<IQuoteStore>()(
  immer((set, get) => ({
    state: {
      isLoading: false,
      assignee: null,
      quote: null,
      quoteMessages: null,
      insuranceProviders: [],
    },
    actions: {
      fetchQuote: async (id) => {
        // const [res, err] = await apiGetQuote(id);
        const [res, err] = await apiGetQuoteDetailInfo(id);

        if (res) {
          set((store) => {
            store.state.quote = res;
          });

          return true;
        }
        return false;
      },
      fetchQuoteMessages: async () => {
        const quoteId = get().state.quote?.id;
        const [res] = await apiGetQuoteMessages(quoteId);

        if (res) {
          set((store) => {
            store.state.quoteMessages = res;
          });
          return true;
        }
        return false;
      },
      fetchAssignee: async () => {
        const assigneeId = get().state.quote?.assigneeId;

        if (!assigneeId) {
          get().actions.setAssignee(null);
          return;
        }
        const [res, err] = await apiGetUser(assigneeId);

        if (res) {
          get().actions.setAssignee(res);
        }
      },
      fetchInsuranceProviders: async () => {
        const { insuranceProviders } = get().state;

        if (!isEmpty(insuranceProviders)) return;

        const [res, err] = await apiGetInsuranceProviders({});

        if (res) {
          set((store) => {
            store.state.insuranceProviders = res.data;
          });
        }
      },
      loadData: async (quoteId) => {
        set((store) => {
          store.state.isLoading = true;
        });

        const { fetchQuote, fetchAssignee, fetchQuoteMessages, fetchInsuranceProviders } = get().actions;

        await fetchQuote(quoteId);
        await fetchAssignee();
        await fetchQuoteMessages();
        await fetchInsuranceProviders();

        set((store) => {
          store.state.isLoading = false;
        });
      },
      initialize: async (quoteId: string) => {
        const { loadData } = get().actions;

        await loadData(quoteId);
      },
      getInsuranceProvider: (providerCode) => {
        if (!providerCode) return null;

        const { insuranceProviders } = get().state;
        const provider = insuranceProviders?.find((provider) => provider.code === providerCode);

        return provider || null;
      },
      updateQuote: async (payload) => {
        set((store) => {
          store.state.isLoading = true;
        });

        const { quote } = get().state;
        const [res, err] = await apiUpdateAdminQuote(quote.id, payload);

        if (res) {
          set((store) => {
            store.state.quote = res;
            store.state.isLoading = false;
          });

          await get().actions.fetchAssignee();
        }
      },
      setAssignee: (user: User | null) => {
        set((store) => {
          store.state.assignee = user;
        });
      },
      createQuoteMessage: async (payload, external) => {
        const { quote } = get().state;

        const createFn = external ? apiCreateQuoteMessage : apiCreateInternalQuoteMessage;

        const [res, err] = await createFn(quote.id, payload);

        if (res) {
          await get().actions.fetchQuoteMessages();
          return true;
        }
        return false;
      },
      updateQuoteMessage: async (messageId, payload) => {
        const { quote } = get().state;
        const [res, err] = await apiUpdateQuoteMessage(messageId, quote.id, payload);

        if (res) {
          await get().actions.fetchQuoteMessages();
          return true;
        }
        return false;
      },
      deleteQuoteMessage: async (messageId) => {
        const { quote } = get().state;
        const [res, err] = await apiDeleteQuoteMessage(messageId, quote.id);

        if (!err) {
          await get().actions.fetchQuoteMessages();
          return true;
        }
        return false;
      },
    },
    clearState: () =>
      set({
        state: {
          isLoading: false,
          quote: null,
          assignee: null,
          quoteMessages: null,
          insuranceProviders: [],
        },
      }),
  }))
);
